<script setup lang="ts">
import {computed} from "vue";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/core/components/ui/dialog";
import {Alert, AlertLevel, AlertLine, AlertSource} from "@/medication-checkup/interfaces/alerts";
import {alertLevelTone} from "@/medication-checkup/services/AlertLevelTones";

/**
 * The whole list of one level of one source, opened from its card: every
 * alert with its type, the treatments it bears on and its body — HTML as
 * the referential writes it, a plain sentence for the AI.
 */

const props = defineProps<{
    /**
     * The level opened — null closes the dialog.
     */
    level: AlertLevel | null;
    source: AlertSource | null;
    lines: AlertLine[];
}>();

const emit = defineEmits<{ close: [] }>();

const tone = computed(() => alertLevelTone(props.level?.severity ?? 'INFO'));

const subtitle = computed(() => [props.source?.label, props.source?.description].filter(Boolean).join(' · '));

const treatments = (alert: Alert): string => {
    return alert.line_ids
        .map(id => props.lines.find(line => line.id === id)?.label ?? null)
        .filter((label): label is string => label !== null)
        .join(' + ');
};
</script>

<template>
  <Dialog :open="level !== null" @update:open="open => !open && emit('close')">
    <DialogContent v-if="level && source" class="max-w-3xl max-h-[90vh] flex flex-col gap-3 p-5">
      <DialogHeader>
        <DialogTitle class="flex items-center gap-2 text-primary">
          <span class="w-2.5 h-2.5 rounded-full" :class="tone.dot" />
          {{ level.label }}
          <span class="rounded-full px-2 py-1 text-xs font-bold" :class="tone.chip">{{ level.count }}</span>
        </DialogTitle>
        <DialogDescription>{{ subtitle }}</DialogDescription>
      </DialogHeader>

      <ul class="flex-1 min-h-0 overflow-y-auto -mx-5 px-5 divide-y divide-gray-light">
        <li
          v-for="alert in level.alerts"
          :key="alert.id"
          class="flex flex-col gap-1 py-3"
        >
          <span class="text-sm font-semibold leading-snug">{{ alert.title }}</span>
          <div class="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
            <span>{{ alert.type_label }}</span>
            <template v-if="treatments(alert)">
              <span aria-hidden="true">·</span>
              <span>{{ treatments(alert) }}</span>
            </template>
          </div>
          <p v-if="!alert.referential && alert.content" class="text-sm leading-relaxed text-gray-dark">
            {{ alert.content }}
          </p>
          <div
            v-if="alert.referential && alert.content" class="text-sm leading-relaxed text-gray-dark space-y-2"
            v-html="alert.content"
          />
          <div
            v-if="alert.referential && alert.detail" class="text-xs leading-relaxed text-muted-foreground space-y-2"
            v-html="alert.detail"
          />
        </li>
      </ul>
    </DialogContent>
  </Dialog>
</template>
